import { FetchResult, gql } from '@apollo/client'
import { handleError } from '../../../common/src/data/errors'
import { addFeedback } from '../../../common/src/feedback'
import {
  JobName,
  RunAdminTaskMutation,
  RunAdminTaskMutationVariables,
  UpdateThreadMutationVariables,
  UpdateThreadMutation,
  SetIsTypingMutationVariables,
  SetIsTypingMutation,
  FetchThreadQuery,
  ContactInput,
  FetchInboxQuery,
  FetchTasksPageQuery,
  FetchOnboardingTasksQuery,
  PostActionMutationVariables,
  PostActionMutation,
  UpdateTasksMutationVariables,
  UpdateTasksMutation,
  RetryActionMutationVariables,
  RetryActionMutation,
  LookAtUserMutationVariables,
  LookAtUserMutation,
} from '../../../common/src/types.gen'
import { apolloWebClient } from '../apolloClientForWeb'
import { Fragments } from './fragments'

const updateTaskMutation = gql`
  mutation UpdateTask($task: LeasingTaskInput!) {
    updateLeasingTask(task: $task) {
      id
      ...LeasingTask
    }
  }
  ${Fragments.leasingTask}
  ${Fragments.contact}
  ${Fragments.address}
  ${Fragments.firm}
`

const updateFirmMutation = gql`
  mutation UpdateFirm($firm: FirmInput!) {
    updateFirm(firm: $firm) {
      ...Firm
    }
  }
  ${Fragments.firm}
`

const updateFirmConfigMutation = gql`
  mutation UpdateFirmConfig($firm: FirmInput!) {
    updateFirm(firm: $firm) {
      ...Firm
      config
    }
  }
  ${Fragments.firm}
`

const updateContactMutation = gql`
  mutation UpdateContact($contact: ContactInput!) {
    updateContact(contact: $contact) {
      ...Contact
    }
  }
  ${Fragments.contact}
`

const updateContactNameMutation = gql`
  mutation UpdateContactName($contact: ContactInput!) {
    updateContact(contact: $contact) {
      id
      firstName
      lastName
    }
  }
`

const updateThreadMutation = gql`
  mutation UpdateThread(
    $threadId: Int!
    $isArchived: Boolean
    $botStatus: ThreadBotStatus
    $lastReadMessageId: Int
    $archiveDays: Int
    $leasingAttributionSource: String
  ) {
    updateThread(
      threadId: $threadId
      isArchived: $isArchived
      botStatus: $botStatus
      lastReadMessageId: $lastReadMessageId
      archiveDays: $archiveDays
      leasingAttributionSource: $leasingAttributionSource
    ) {
      id
      isArchived
      archiveUntil
      botStatus
      lastMessageId
      lastReadMessageId
    }
  }
`

const setIsTypingMutation = gql`
  mutation SetIsTyping($threadId: Int!, $isTyping: Boolean!) {
    setIsTyping(threadId: $threadId, isTyping: $isTyping)
  }
`

const postActionMutation = gql`
  mutation PostAction($threadId: Int!, $action: ActionInput!, $actionCount: Int) {
    postAction(threadId: $threadId, action: $action, actionCount: $actionCount) {
      ...ThreadMessages
    }
  }
  ${Fragments.threadMessages}
  ${Fragments.message}
  ${Fragments.messagesWithLinks}
  ${Fragments.contact}
  ${Fragments.botThread}
  ${Fragments.actionToExec}
`

const retryActionMutation = gql`
  mutation RetryAction($threadId: Int!, $actionId: Int!) {
    retryAction(threadId: $threadId, actionId: $actionId) {
      id
    }
  }
`

const runAdminTaskMutation = gql`
  mutation RunAdminTask($argumentsJson: String!) {
    runAdminTask(argumentsJson: $argumentsJson)
  }
`

const bulkUpdateTasksMutation = gql`
  mutation UpdateTasks($taskIds: [Int!]!, $field: BulkUpdateOp!, $value: String) {
    bulkUpdateTasks(taskIds: $taskIds, field: $field, value: $value) {
      id
      displayName
      offboardDate
      launchDate
      taskType
      isArchived
      leasingInfo {
        propertyType
      }
    }
  }
`

const lookAtUserMutation = gql`
  mutation LookAtUser($observedUserId: Int) {
    setLookingAtUser(observedUserId: $observedUserId) {
      id
      ...LookingAtFragment
    }
  }
  ${Fragments.lookingAtFragment}
`

export function runAdminTaskUncaught<T extends { name: JobName }>(args: T): Promise<FetchResult<RunAdminTaskMutation>> {
  const variables = { argumentsJson: JSON.stringify(args) }
  return apolloWebClient.mutate<RunAdminTaskMutation, RunAdminTaskMutationVariables>({
    mutation: runAdminTaskMutation,
    variables,
  })
}

export function updateThread(variables: UpdateThreadMutationVariables, feedbackMessage?: string): Promise<void> {
  return apolloWebClient
    .mutate<UpdateThreadMutation, UpdateThreadMutationVariables>({
      mutation: updateThreadMutation,
      variables,
    })
    .then(() => maybeAddFeedback(feedbackMessage))
    .catch(handleError)
}

export function setIsTyping(variables: SetIsTypingMutationVariables): Promise<any> {
  return apolloWebClient
    .mutate<SetIsTypingMutation, SetIsTypingMutationVariables>({
      mutation: setIsTypingMutation,
      variables,
    })
    .catch(handleError)
}

export function updateContact(
  contactThem: FetchThreadQuery['thread']['contactThem'],
  input: ContactInput
): Promise<void> {
  const newContact = Object.assign({}, contactThem, input)
  return apolloWebClient
    .mutate({
      mutation: updateContactMutation,
      variables: {
        contact: input,
      },
      optimisticResponse: {
        updateContact: newContact,
      },
    })
    .then(() => addFeedback('Saved contact'))
    .catch(handleError)
}

export function updateContactName(input: ContactInput) {
  const { id, firstName, lastName } = input
  return apolloWebClient
    .mutate({
      mutation: updateContactNameMutation,
      variables: {
        contact: input,
      },
      optimisticResponse: {
        updateContact: {
          __typename: 'Contact',
          id,
          firstName,
          lastName,
        },
      },
    })
    .then(() => addFeedback('Saved contact'))
    .catch(handleError)
}

export function updateTask(
  task:
    | NonNullable<FetchInboxQuery['thread']>['leasingTask']
    | NonNullable<FetchTasksPageQuery['task']>
    | FetchOnboardingTasksQuery['onboardingTasks'][0],
  key: string,
  value: string | number,
  predictResponse: boolean
): Promise<void> {
  const input: { [key: string]: string | number } = { id: task.id }
  input[key] = value

  let optimisticResponse
  if (predictResponse) {
    const expectedTask = Object.assign({}, task, input)
    expectedTask.leasingInfo = Object.assign({}, task.leasingInfo, input)
    optimisticResponse = {
      updateLeasingTask: expectedTask,
    }
  }

  console.log('Update Task', {
    firmId: task.firm.id,
    firmName: task.firm.firmName,
    taskName: task.displayName,
    taskId: task.id,
    key,
    value,
  })

  return apolloWebClient
    .mutate({
      mutation: updateTaskMutation,
      variables: {
        task: input,
      },
      optimisticResponse,
    })
    .then(() => addFeedback('Saved task'))
    .catch(handleError)
}

export function updateFirm(
  firm:
    | NonNullable<FetchInboxQuery['thread']>['leasingTask']['firm']
    | NonNullable<FetchTasksPageQuery['task']>['firm'],
  key: string,
  value: any,
  updateConfig?: boolean
): Promise<void> {
  const input: any = { id: firm.id }
  input[key] = value

  const optimisticUpdate = Object.assign({}, firm, { [key]: value })

  return apolloWebClient
    .mutate({
      mutation: updateConfig ? updateFirmConfigMutation : updateFirmMutation,
      variables: {
        firm: input,
      },
      optimisticResponse: {
        updateFirm: optimisticUpdate,
      },
    })
    .then(() => addFeedback('Saved firm'))
    .catch(handleError)
}

export function postActionUncaught(variables: PostActionMutationVariables): Promise<FetchResult<PostActionMutation>> {
  console.log('Action', variables.action)
  return apolloWebClient.mutate({
    mutation: postActionMutation,
    variables,
  })
}

export function postAction(variables: PostActionMutationVariables, feedbackMessage?: string): Promise<void> {
  return postActionUncaught(variables)
    .then(() => maybeAddFeedback(feedbackMessage))
    .catch(handleError)
}

export function retryAction(variables: RetryActionMutationVariables): Promise<FetchResult<RetryActionMutation>> {
  return apolloWebClient.mutate({
    mutation: retryActionMutation,
    variables,
  })
}

export function bulkUpdateTasks(variables: UpdateTasksMutationVariables): Promise<FetchResult<UpdateTasksMutation>> {
  return apolloWebClient.mutate({
    mutation: bulkUpdateTasksMutation,
    variables,
  })
}

export function lookAtUser(variables: LookAtUserMutationVariables): Promise<FetchResult<LookAtUserMutation>> {
  console.log('Change Inbox View', variables)
  return apolloWebClient.mutate({
    mutation: lookAtUserMutation,
    variables,
  })
}

function maybeAddFeedback(feedbackMessage?: string) {
  if (feedbackMessage) {
    addFeedback(feedbackMessage)
  }
}
